import { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapboxMapHideAttributionClass } from '../utils/mapboxRtl';
import { acquireSharedMap, onSharedMapReady, releaseSharedMap } from '../utils/sharedMapInstance';
import { TRIP_MAP } from '../config/constants';

const { SPEED_SLOW_KMH, SPEED_MEDIUM_KMH, REPLAY_DELAY_SLOW_MS, REPLAY_DELAY_NORMAL_MS, REPLAY_DELAY_FAST_MS } =
  TRIP_MAP;

const REPLAY_SPEEDS = {
  slow: REPLAY_DELAY_SLOW_MS,
  normal: REPLAY_DELAY_NORMAL_MS,
  fast: REPLAY_DELAY_FAST_MS,
};

function headingLabel(deg) {
  if (deg == null) return '—';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
  return dirs[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
}

function speedColor(spd) {
  if (spd <= SPEED_SLOW_KMH) return '#22c55e';
  if (spd <= SPEED_MEDIUM_KMH) return '#f97316';
  return '#ef4444';
}

function buildTraceGeoJSON(positions) {
  const groups = [];
  let cur = null;
  for (let i = 0; i < positions.length; i++) {
    const color = speedColor(positions[i].speed ?? 0);
    if (!cur || cur.color !== color) {
      if (cur) groups.push(cur);
      cur = { color, pts: [positions[i]] };
    } else {
      cur.pts.push(positions[i]);
    }
    if (i < positions.length - 1 && speedColor(positions[i + 1].speed ?? 0) !== color) {
      cur.pts.push(positions[i + 1]);
    }
  }
  if (cur) groups.push(cur);

  return {
    type: 'FeatureCollection',
    features: groups
      .filter((g) => g.pts.length >= 2)
      .map(({ color, pts }) => ({
        type: 'Feature',
        properties: { color },
        geometry: {
          type: 'LineString',
          coordinates: pts.map((p) => [p.lng, p.lat]),
        },
      })),
  };
}

function createStopEl(index, status) {
  const completed = status === 'completed' || status === 'visited';
  const el = document.createElement('div');
  el.style.cssText = `
    width:26px;height:26px;border-radius:50%;
    background:${completed ? '#22c55e' : '#f97316'};
    color:white;font-size:11px;font-weight:700;
    display:flex;align-items:center;justify-content:center;
    border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.35);
    cursor:pointer;
  `;
  el.textContent = String(index + 1);
  return el;
}

const TripReplayMap = ({ positions = [], stops = [], height = '320px' }) => {
  const outerRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const busMarkerRef = useRef(null);
  const animRef = useRef(null);
  const speedDelayRef = useRef(REPLAY_DELAY_SLOW_MS);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState('slow');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Init carte — instance Mapbox partagée (reuseMap) ──────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const initCenter = positions.length ? [positions[0].lng, positions[0].lat] : [-7.5898, 33.5731];

    // container plein écran : outerRef = wrapper entier (carte + panel)
    const owner = {};
    mapRef.current = acquireSharedMap(
      containerRef.current,
      {
        center: initCenter,
        zoom: 12,
        fullscreenContainer: outerRef.current,
      },
      owner,
    );

    let cancelled = false;
    onSharedMapReady(() => {
      if (cancelled) return;
      mapRef.current?.resize();
      setMapLoaded(true);
    });

    const ro = new ResizeObserver(() => mapRef.current?.resize());
    ro.observe(containerRef.current);

    // Resize après 500ms — le Dialog Radix peut avoir fini son animation after mount
    const resizeTimer = setTimeout(() => mapRef.current?.resize(), 500);

    return () => {
      cancelled = true;
      clearTimeout(resizeTimer);
      ro.disconnect();
      clearInterval(animRef.current);
      markersRef.current.forEach((m) => {
        try {
          m.remove();
        } catch (_e) {
          /* cleanup */
        }
      });
      markersRef.current = [];
      busMarkerRef.current?.remove();
      busMarkerRef.current = null;
      releaseSharedMap(owner);
      mapRef.current = null;
      if (!cancelled) setMapLoaded(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Détection fullscreen ──────────────────────────────────────────────────
  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Laisser le browser finir la transition avant de recalculer la taille du canvas
      setTimeout(() => mapRef.current?.resize(), 100);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // ── Trace + marqueurs quand carte prête et positions dispo ────────────────
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || positions.length < 2) return;
    const map = mapRef.current;

    if (map.getLayer('trip-trace')) map.removeLayer('trip-trace');
    if (map.getSource('trip-trace')) map.removeSource('trip-trace');
    markersRef.current.forEach((m) => {
      try {
        m.remove();
      } catch (_e) {
        /* cleanup */
      }
    });
    markersRef.current = [];
    busMarkerRef.current?.remove();

    map.addSource('trip-trace', {
      type: 'geojson',
      data: buildTraceGeoJSON(positions),
    });
    map.addLayer({
      id: 'trip-trace',
      type: 'line',
      source: 'trip-trace',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 4,
        'line-opacity': 0.9,
      },
    });

    const startEl = document.createElement('div');
    startEl.textContent = '🚌';
    startEl.style.cssText = 'font-size:22px;cursor:pointer;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4))';
    markersRef.current.push(
      new mapboxgl.Marker({ element: startEl, anchor: 'bottom' })
        .setLngLat([positions[0].lng, positions[0].lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 10 }).setHTML(
            '<div style="font-weight:600;font-size:13px">🚌 Départ du trajet</div>',
          ),
        )
        .addTo(map),
    );

    const endEl = document.createElement('div');
    endEl.textContent = '🏫';
    endEl.style.cssText = 'font-size:22px;cursor:pointer;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4))';
    markersRef.current.push(
      new mapboxgl.Marker({ element: endEl, anchor: 'bottom' })
        .setLngLat([positions[positions.length - 1].lng, positions[positions.length - 1].lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 10 }).setHTML(
            '<div style="font-weight:600;font-size:13px">🏫 Arrivée à l\'école</div>',
          ),
        )
        .addTo(map),
    );

    stops.forEach((stop, i) => {
      const el = createStopEl(i, stop.status);
      const statusColor = stop.status === 'completed' ? '#22c55e' : stop.status === 'skipped' ? '#94a3b8' : '#f97316';
      const statusLabel =
        stop.status === 'completed' ? '✅ Complété' : stop.status === 'skipped' ? '⏭️ Passé' : '⏳ En attente';
      const timeStr = stop.markedAt
        ? (() => {
            try {
              const d = new Date(stop.markedAt);
              return isNaN(d.getTime())
                ? null
                : d.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
            } catch {
              return null;
            }
          })()
        : null;
      const popup = new mapboxgl.Popup({ offset: 16 }).setHTML(`
        <div style="padding:4px 6px;font-family:system-ui;min-width:170px">
          <div style="font-weight:700;font-size:13px;margin-bottom:3px">Arrêt ${i + 1} · ${stop.name || '—'}</div>
          <div style="font-size:12px;color:${statusColor};margin-bottom:${timeStr ? '2px' : '0'}">${statusLabel}</div>
          ${timeStr ? `<div style="font-size:11px;color:#64748b">🕐 Pointé à ${timeStr}</div>` : ''}
        </div>
      `);
      markersRef.current.push(
        new mapboxgl.Marker({ element: el }).setLngLat([stop.lng, stop.lat]).setPopup(popup).addTo(map),
      );
    });

    const busEl = document.createElement('div');
    busEl.style.cssText =
      'width:14px;height:14px;border-radius:50%;background:#3b82f6;border:3px solid white;box-shadow:0 0 0 2px #3b82f6;';
    busMarkerRef.current = new mapboxgl.Marker({ element: busEl })
      .setLngLat([positions[0].lng, positions[0].lat])
      .addTo(map);

    const lngs = positions.map((p) => p.lng);
    const lats = positions.map((p) => p.lat);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 50, duration: 800 },
    );

    setCurrentIdx(0);
    setIsPlaying(false);
    clearInterval(animRef.current);
  }, [mapLoaded, positions, stops]);

  // ── Déplacer le marqueur bus sur le slider ────────────────────────────────
  useEffect(() => {
    if (!busMarkerRef.current || !positions[currentIdx]) return;
    const { lng, lat } = positions[currentIdx];
    busMarkerRef.current.setLngLat([lng, lat]);
  }, [currentIdx, positions]);

  // ── Lecture / pause du replay ─────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    if (isPlaying) {
      clearInterval(animRef.current);
      setIsPlaying(false);
    } else {
      if (currentIdx >= positions.length - 1) setCurrentIdx(0);
      setIsPlaying(true);
      animRef.current = setInterval(() => {
        setCurrentIdx((prev) => {
          if (prev >= positions.length - 1) {
            clearInterval(animRef.current);
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, speedDelayRef.current);
    }
  }, [isPlaying, currentIdx, positions.length]);

  // ── Changement de vitesse (relance l'interval si en cours) ───────────────
  const handleSpeedChange = useCallback(
    (newSpeed) => {
      speedDelayRef.current = REPLAY_SPEEDS[newSpeed];
      setReplaySpeed(newSpeed);
      if (isPlaying) {
        clearInterval(animRef.current);
        animRef.current = setInterval(() => {
          setCurrentIdx((prev) => {
            if (prev >= positions.length - 1) {
              clearInterval(animRef.current);
              setIsPlaying(false);
              return prev;
            }
            return prev + 1;
          });
        }, REPLAY_SPEEDS[newSpeed]);
      }
    },
    [isPlaying, positions.length],
  );

  const handleSlider = useCallback((e) => {
    clearInterval(animRef.current);
    setIsPlaying(false);
    setCurrentIdx(Number(e.target.value));
  }, []);

  // ── Données trame courante ────────────────────────────────────────────────
  const currentPos = positions[currentIdx];
  const timeLabel = currentPos?.timestamp
    ? new Date(currentPos.timestamp).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';

  const nextStop = stops.find((stop) => {
    if (!stop.markedAt || !currentPos?.timestamp) return false;
    return new Date(stop.markedAt) > new Date(currentPos.timestamp);
  });

  // ── Boutons vitesse (partagés barre normale + panel fullscreen) ───────────
  const SpeedButtons = () => (
    <div className="flex items-center gap-0.5 shrink-0">
      {[
        ['slow', '0.5×'],
        ['normal', '1×'],
        ['fast', '2×'],
      ].map(([key, label]) => (
        <button
          key={key}
          onClick={() => handleSpeedChange(key)}
          className={`px-1.5 py-0.5 rounded text-xs font-semibold transition-colors
            ${replaySpeed === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div
      ref={outerRef}
      className={`relative w-full overflow-hidden rounded-t-lg ${mapboxMapHideAttributionClass}`}
      style={{ height, flexShrink: 0 }}
    >
      <div ref={containerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />

      {/* Légende vitesse */}
      <div className="absolute bottom-12 left-2 flex items-center gap-2 rounded-md bg-white/90 px-2 py-1 text-xs shadow backdrop-blur-sm z-10 pointer-events-none">
        <span className="flex items-center gap-1">
          <span className="inline-block h-1.5 w-4 rounded-full bg-green-500" /> Lent
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-1.5 w-4 rounded-full bg-orange-400" /> Normal
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-1.5 w-4 rounded-full bg-red-500" /> Rapide
        </span>
        <span className="mx-1 border-l border-gray-300 h-3" />
        <span className="flex items-center gap-1">🚌 Départ</span>
        <span className="flex items-center gap-1">🏫 École</span>
      </div>

      {positions.length > 1 &&
        (isFullscreen ? (
          /* ── Panel fullscreen ──────────────────────────────────────────── */
          <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm z-10 px-4 py-3 flex flex-col gap-2">
            {/* Ligne 1 : play + slider + boutons vitesse */}
            <div className="flex items-center gap-3">
              <button
                onClick={togglePlay}
                className="shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-400 transition-colors text-sm font-bold"
                title={isPlaying ? 'Pause' : 'Lecture'}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              <input
                type="range"
                min={0}
                max={positions.length - 1}
                value={currentIdx}
                onChange={handleSlider}
                className="flex-1 h-1.5 accent-blue-400 cursor-pointer"
              />
              <SpeedButtons />
            </div>

            {/* Ligne 2 : infos trame */}
            <div className="flex items-center gap-4 text-xs text-white/80 flex-wrap">
              <span className="font-mono tabular-nums">🕐 {timeLabel}</span>
              <span>🚀 {Math.round(currentPos?.speed ?? 0)} km/h</span>
              <span>
                🧭 {headingLabel(currentPos?.heading)} ({Math.round(currentPos?.heading ?? 0)}°)
              </span>
              <span className="font-mono">
                📍 {currentPos?.lat?.toFixed(4)}, {currentPos?.lng?.toFixed(4)}
              </span>
              <span className="text-white/50">
                Trame {currentIdx + 1} / {positions.length}
              </span>
              {nextStop && (
                <span className="text-blue-300">
                  ⏭ Prochain arrêt : {nextStop.name || `Arrêt ${stops.indexOf(nextStop) + 1}`}
                </span>
              )}
            </div>
          </div>
        ) : (
          /* ── Barre de contrôle normale ─────────────────────────────────── */
          <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm px-3 py-1.5 flex items-center gap-2 z-10 border-t border-gray-100">
            <button
              onClick={togglePlay}
              className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors text-xs font-bold"
              title={isPlaying ? 'Pause' : 'Lecture'}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <input
              type="range"
              min={0}
              max={positions.length - 1}
              value={currentIdx}
              onChange={handleSlider}
              className="flex-1 h-1 accent-blue-600 cursor-pointer"
            />
            <SpeedButtons />
            <span className="shrink-0 text-xs text-gray-500 tabular-nums w-28 text-right">
              {Math.round(currentPos?.speed ?? 0)} km/h · {timeLabel}
            </span>
          </div>
        ))}
    </div>
  );
};

export default TripReplayMap;
